import { useEffect, useState } from 'react';
import { Modale } from '../ui/Modale';
import { BoutonSecondaire, MessageErreur, MarqueObligatoire } from '../ui/Primitives';

/** Motifs les plus fréquents, pour éviter d'écrire la même phrase dix fois. */
const MOTIFS_COURANTS = [
  'Document illisible ou trop sombre',
  'Document incomplet : il manque une page',
  'Document expiré',
  'Ce n’est pas le document demandé',
  'Le nom ne correspond pas à celui du dossier',
] as const;

/**
 * Refus motivé d'une pièce justificative (CDC §4.3).
 *
 * L'API exige un motif — `schemaControlePiece` refuse un `REFUSEE` sans
 * `motifRefus` — et c'est ce qui compte pour le candidat : il voit le motif dans
 * son espace, sans quoi il redéposerait le même document. Le motif est donc
 * rédigé pour être lu par lui, pas comme une note interne.
 */
export function RefusPiece({
  ouverte,
  libellePiece,
  enCours,
  erreur,
  onRefuser,
  onFermer,
}: {
  ouverte: boolean;
  libellePiece: string;
  enCours: boolean;
  erreur: string | null;
  onRefuser: (motif: string) => void;
  onFermer: () => void;
}): JSX.Element {
  const [motif, setMotif] = useState('');

  useEffect(() => {
    if (ouverte) setMotif('');
  }, [ouverte]);

  const envoyer = (evenement: React.FormEvent): void => {
    evenement.preventDefault();
    const propre = motif.trim();
    if (!propre) return;
    onRefuser(propre);
  };

  return (
    <Modale
      titre="Refuser cette pièce"
      sousTitre={`${libellePiece} · le motif est transmis au candidat, qui pourra redéposer`}
      ouverte={ouverte}
      onFermer={onFermer}
      largeur="max-w-xl"
    >
      <form onSubmit={envoyer} className="space-y-4 px-4 sm:px-6 py-5">
        {erreur && <MessageErreur message={erreur} />}

        <div>
          <span className="mb-2 block text-[11px] font-bold uppercase tracking-widest text-codialis-gray">
            Motifs courants
          </span>
          <div className="flex flex-wrap gap-2">
            {MOTIFS_COURANTS.map((courant) => (
              <button
                key={courant}
                type="button"
                onClick={() => setMotif(courant)}
                aria-pressed={motif === courant}
                // 38 px de haut : reste confortable au doigt sans écraser la
                // mise en page quand les motifs passent sur trois lignes.
                className={`min-h-[38px] [@media(pointer:coarse)]:min-h-[40px] rounded-lg border px-3 py-2 text-left text-xs font-medium transition ${
                  motif === courant
                    ? 'border-codialis-navy bg-codialis-navy text-white'
                    : 'border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
                }`}
              >
                {courant}
              </button>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-codialis-navy">
            Motif transmis au candidat<MarqueObligatoire />
          </span>
          <textarea
            required
            rows={3}
            maxLength={500}
            value={motif}
            onChange={(evenement) => setMotif(evenement.target.value)}
            placeholder="Précisez ce qui doit être corrigé."
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-codialis-navy focus:outline-none focus:ring-2 focus:ring-codialis-mint"
          />
          <span className="mt-1 block text-[11px] text-codialis-gray">
            {motif.trim().length}/500 caractères. Un motif clair évite un second refus.
          </span>
        </label>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-4">
          <BoutonSecondaire onClick={onFermer} disabled={enCours}>
            Annuler
          </BoutonSecondaire>
          <button
            type="submit"
            disabled={enCours || motif.trim() === ''}
            className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {enCours ? 'Enregistrement…' : 'Refuser la pièce'}
          </button>
        </div>
      </form>
    </Modale>
  );
}
